/**
 * File Upload Service
 * Handles image uploads to backend server
 */

import axios from 'axios';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '/api';
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

export const normalizeUploadUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url)) return url;

  const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Upload images to server
 * @param {File[]} files - Array of image files
 * @returns {Promise} - Response with image URLs
 */
export const uploadImages = async (files) => {
  try {
    if (!files || files.length === 0) {
      throw new Error('No files provided');
    }

    // Create FormData
    const formData = new FormData();
    files.forEach((file, index) => {
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error(`File ${index + 1} is not an image`);
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`File ${index + 1} is too large (max 5MB)`);
      }
      formData.append('images', file);
    });

    // Upload to server
    const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.data.success) {
      return (response.data.imageUrls || []).map(normalizeUploadUrl);
    } else {
      throw new Error(response.data.error || 'Upload failed');
    }
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};

/**
 * Convert File to base64
 * @param {File} file - Image file
 * @returns {Promise} - Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * Convert multiple files to base64
 * @param {File[]} files - Array of image files
 * @returns {Promise} - Array of base64 strings
 */
export const filesToBase64 = async (files) => {
  try {
    const promises = Array.from(files).map(file => fileToBase64(file));
    return await Promise.all(promises);
  } catch (error) {
    console.error('Error converting files to base64:', error);
    throw error;
  }
};

/**
 * Validate image file
 * @param {File} file - Image file
 * @returns {Object} - Validation result
 */
export const validateImageFile = (file) => {
  const errors = [];

  // Check file type
  if (!file.type.startsWith('image/')) {
    errors.push('File must be an image (JPG, PNG, etc.)');
  }

  // Check file size
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    errors.push('File size must be less than 5MB');
  }

  return {
    valid: errors.length === 0,
    errors: errors
  };
};

/**
 * Get file size in MB
 * @param {File} file - Image file
 * @returns {string} - Formatted file size
 */
export const getFileSizeMB = (file) => {
  return (file.size / (1024 * 1024)).toFixed(2);
};

/**
 * Create image preview from file
 * @param {File} file - Image file
 * @returns {Promise} - Preview data URL
 */
export const createImagePreview = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Compress image before upload
 * @param {File} file - Image file
 * @param {number} maxWidth - Max width in pixels
 * @param {number} maxHeight - Max height in pixels
 * @returns {Promise} - Compressed image blob
 */
export const compressImage = (file, maxWidth = 1200, maxHeight = 1200) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * (maxHeight / height));
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          'image/jpeg',
          0.8 // Quality 80%
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Get image dimensions
 * @param {File} file - Image file
 * @returns {Promise} - Object with width and height
 */
export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };

      img.onerror = () => reject(new Error('Failed to load image'));
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const fileUploadService = {
  uploadImages,
  fileToBase64,
  filesToBase64,
  validateImageFile,
  getFileSizeMB,
  createImagePreview,
  compressImage,
  getImageDimensions
};

export default fileUploadService;
